import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  MenuItem,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { userValidationSchema } from '../utils/validationSchemas';
import { hasFieldError, getFieldHelperText } from '../hooks/useFormValidation';
import * as yup from 'yup';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useNotification } from '../contexts/NotificationContext';
import { useFormSubmissionRateLimit } from '../hooks/useRateLimit';
import { sanitizeInput } from '../utils/sanitization';
import api from '../utils/api';

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'on_leave', label: 'On Leave' },
  { value: 'terminated', label: 'Terminated' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Toronto', label: 'Eastern Time - Canada' },
  { value: 'America/Vancouver', label: 'Pacific Time - Canada' },
  { value: 'America/Halifax', label: 'Atlantic Time - Canada' },
  { value: 'America/Winnipeg', label: 'Central Time - Canada' },
  { value: 'America/Edmonton', label: 'Mountain Time - Canada' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Europe/Rome', label: 'Central European Time (CET)' },
  { value: 'Europe/Madrid', label: 'Central European Time (CET)' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (CET)' },
  { value: 'Europe/Brussels', label: 'Central European Time (CET)' },
  { value: 'Europe/Zurich', label: 'Central European Time (CET)' },
  { value: 'Europe/Stockholm', label: 'Central European Time (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (EET)' },
  { value: 'Europe/Athens', label: 'Eastern European Time (EET)' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time (MSK)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore Standard Time (SGT)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time (AET)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWT)' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time (ACT)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (NZST)' },
];

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void | Promise<void>;
  initialData?: any;
  onArchive?: (userId: number) => void;
  onUnarchive?: (userId: number) => void;
  onResendInvitation?: (userId: number) => Promise<void>;
}

const UserForm: React.FC<UserFormProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  initialData, 
  onArchive, 
  onUnarchive,
  onResendInvitation
}) => {
  const [departments, setDepartments] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const { error, handleError, clearError } = useErrorHandler();
  const { showSuccess, showError } = useNotification();
  const { isLimited: isRateLimited, recordAttempt, remainingAttempts } = useFormSubmissionRateLimit();

  // Let TypeScript infer the type from resolver and defaultValues
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(userValidationSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      role: 'employee',
      employment_status: 'active',
      phone_number: '',
      employee_id: '',
      timezone: 'America/Los_Angeles',
      hire_date: '',
      department_id: null,
      location_id: null,
    },
    mode: 'onChange',
  });

  // Clear an API error only after the person edits the form.
  const watchedSnapshot = JSON.stringify(watch());

  useEffect(() => {
    clearError();
  }, [watchedSnapshot, clearError]);

  // Ensure departments and locations are always arrays
  const safeDepartments = Array.isArray(departments) ? departments : [];
  const safeLocations = Array.isArray(locations) ? locations : [];

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const [deptResponse, locResponse] = await Promise.all([
        api.get('/departments/'),
        api.get('/locations/')
      ]);
      
      const deptData = deptResponse.data.results || deptResponse.data || [];
      const locData = locResponse.data.results || locResponse.data || [];
      
      setDepartments(deptData);
      setLocations(locData);
    } catch (error) {
      console.error('Error fetching options:', error);
      setDepartments([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchOptions();
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      // Convert department and location objects to IDs for the form
      const formData = {
        ...initialData,
        department_id: initialData.department?.id || null,
        location_id: initialData.location?.id || null,
        hire_date: initialData.hire_date || '',
        phone_number: initialData.phone_number || '',
        employee_id: initialData.employee_id || '',
      };
      reset(formData);
    } else {
      reset({
        email: '',
        first_name: '',
        last_name: '',
        role: 'employee',
        employment_status: 'active',
        phone_number: '',
        employee_id: '',
        timezone: 'America/Los_Angeles',
        hire_date: '',
        department_id: null,
        location_id: null,
      });
    }
  }, [initialData, open, reset]);

  const onFormSubmit = async (data: any) => {
    try {
      clearError();
      
      // Check rate limiting
      if (isRateLimited) {
        showError(`Too many form submissions. Please wait before trying again. (${remainingAttempts} attempts remaining)`);
        return;
      }
      
      // Record attempt for rate limiting
      recordAttempt();
      
      // Sanitize all input data
      const blankToNull = (value: unknown) => {
        if (value == null) return null;
        const trimmed = String(value).trim();
        return trimmed ? trimmed : null;
      };

      const sanitizedData = {
        ...data,
        email: sanitizeInput(data.email, 254),
        first_name: sanitizeInput(data.first_name, 150),
        last_name: sanitizeInput(data.last_name, 150),
        phone_number: data.phone_number ? sanitizeInput(data.phone_number, 20) : '',
        employee_id: blankToNull(data.employee_id ? sanitizeInput(data.employee_id, 50) : null),
        hire_date: blankToNull(data.hire_date),
        timezone: sanitizeInput(data.timezone, 50),
        department: data.department_id ? parseInt(String(data.department_id)) : null,
        location: data.location_id ? parseInt(String(data.location_id)) : null,
      };
      
      await onSubmit(sanitizedData);
      showSuccess(initialData ? 'User updated successfully!' : 'User created successfully!');
    } catch (err: any) {
      handleError(err);
    }
  };

  const handleArchive = async () => {
    if (initialData?.id && onArchive) {
      try {
        clearError();
        await onArchive(initialData.id);
        showSuccess('User archived successfully!');
      } catch (err: any) {
        handleError(err);
      }
    }
  };

  const handleUnarchive = async () => {
    if (initialData?.id && onUnarchive) {
      try {
        clearError();
        await onUnarchive(initialData.id);
        showSuccess('User unarchived successfully!');
      } catch (err: any) {
        handleError(err);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Edit User' : 'Add User'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Email"
                fullWidth
                required
                error={hasFieldError(errors, 'email')}
                helperText={getFieldHelperText(errors, 'email', 'User\'s email address')}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="first_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="First Name"
                fullWidth
                required
                error={hasFieldError(errors, 'first_name')}
                helperText={getFieldHelperText(errors, 'first_name')}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="last_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Last Name"
                fullWidth
                required
                error={hasFieldError(errors, 'last_name')}
                helperText={getFieldHelperText(errors, 'last_name')}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Role"
                select
                fullWidth
                required
                error={hasFieldError(errors, 'role')}
                helperText={getFieldHelperText(errors, 'role')}
                disabled={isSubmitting}
              >
                {ROLE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="employment_status"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Employment Status"
                select
                fullWidth
                required
                error={hasFieldError(errors, 'employment_status')}
                helperText={getFieldHelperText(errors, 'employment_status')}
                disabled={isSubmitting}
              >
                {EMPLOYMENT_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="department_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Department"
                select
                fullWidth
                disabled={loading || isSubmitting}
                error={hasFieldError(errors, 'department_id')}
                helperText={getFieldHelperText(errors, 'department_id')}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
              >
                <MenuItem value="">
                  <em>Select a department</em>
                </MenuItem>
                {safeDepartments.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="location_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Location"
                select
                fullWidth
                disabled={loading || isSubmitting}
                error={hasFieldError(errors, 'location_id')}
                helperText={getFieldHelperText(errors, 'location_id')}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
              >
                <MenuItem value="">
                  <em>Select a location</em>
                </MenuItem>
                {safeLocations.map(loc => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="hire_date"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Hire Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={hasFieldError(errors, 'hire_date')}
                helperText={getFieldHelperText(errors, 'hire_date')}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="phone_number"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Phone Number"
                fullWidth
                error={hasFieldError(errors, 'phone_number')}
                helperText={getFieldHelperText(errors, 'phone_number', 'Optional phone number')}
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Time Zone"
                select
                fullWidth
                required
                error={hasFieldError(errors, 'timezone')}
                helperText={getFieldHelperText(errors, 'timezone')}
                disabled={isSubmitting}
              >
                {TIMEZONE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="employee_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Employee ID"
                fullWidth
                error={hasFieldError(errors, 'employee_id')}
                helperText={getFieldHelperText(
                  errors, 
                  'employee_id', 
                  'Optional unique employee identifier'
                )}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        {initialData && onArchive && onUnarchive && (
          <>
            {initialData.archived ? (
              <Button 
                onClick={handleUnarchive} 
                variant="outlined" 
                color="success"
                disabled={isSubmitting}
              >
                Unarchive User
              </Button>
            ) : (
              <Button 
                onClick={handleArchive} 
                variant="outlined" 
                color="error"
                disabled={isSubmitting}
              >
                Archive User
              </Button>
            )}
          </>
        )}
        {initialData && onResendInvitation && (initialData.invitation_status === 'pending' || initialData.invitation_status === 'expired') && (
          <Button 
            onClick={async () => {
              setResendingInvitation(true);
              try {
                await onResendInvitation(initialData.id);
                showSuccess('Invitation email resent successfully');
              } catch (error) {
                showError('Failed to resend invitation email');
              } finally {
                setResendingInvitation(false);
              }
            }} 
            variant="outlined" 
            color="primary"
            disabled={isSubmitting || resendingInvitation}
          >
            {resendingInvitation ? <CircularProgress size={20} /> : 'Resend Invitation'}
          </Button>
        )}
        <Button 
          onClick={handleSubmit(onFormSubmit)} 
          variant="contained"
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? (
            <CircularProgress size={20} />
          ) : (
            initialData ? 'Save' : 'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserForm;